import React, { useState, useEffect } from 'react';
import { Book, Code, Copy, Check, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

const APIDocumentation = () => {
  const [statuts, setStatuts] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedSection, setExpandedSection] = useState('create');
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Charger les statuts dynamiquement
    fetch(`${baseUrl}/api/config/statuts`)
      .then(res => res.json())
      .then(data => setStatuts(data))
      .catch(err => console.error(err));
  }, [baseUrl]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, language = 'bash', id }) => (
    <div className="relative group">
      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 p-2 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition"
      >
        {copiedCode === id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-300" />}
      </button>
    </div>
  );

  const Section = ({ id, title, children }) => (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpandedSection(expandedSection === id ? null : id)}
        className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition"
      >
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {expandedSection === id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {expandedSection === id && (
        <div className="p-6 space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Book className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">API SocialAC</h1>
              <p className="text-green-100">Documentation v2.0</p>
            </div>
          </div>
          <p className="text-lg text-green-50 max-w-2xl">
            Intégrez votre CRM avec SocialAC pour créer des dossiers, suivre leur statut et synchroniser les commentaires.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Quick Start */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">🚀 Démarrage rapide</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">URL de base</h3>
              <code className="text-blue-700 text-sm break-all">{baseUrl}/api/webhook</code>
            </div>
            <div className="bg-purple-50 rounded-xl p-6">
              <h3 className="font-semibold text-purple-900 mb-2">Authentification</h3>
              <code className="text-purple-700 text-sm">X-API-Key: sk_live_votre_cle</code>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-800 text-sm">
              <strong>⚠️ Important :</strong> Obtenez votre clé API auprès de l'administrateur SocialAC. Ne partagez jamais votre clé.
            </p>
          </div>
        </div>

        {/* Statuts */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Statuts disponibles</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Icône</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Code</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Label</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Description</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Final</th>
                </tr>
              </thead>
              <tbody>
                {statuts.map((s) => (
                  <tr key={s.code} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-2xl">{s.icone}</td>
                    <td className="py-3 px-4">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm">{s.code}</code>
                    </td>
                    <td className="py-3 px-4 font-medium" style={{ color: s.couleur }}>{s.label}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">{s.description}</td>
                    <td className="py-3 px-4">
                      {s.is_final ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Oui</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Non</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Endpoints */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">📡 Endpoints</h2>

          <Section id="create" title="POST /dossiers - Créer un dossier">
            <p className="text-gray-600 mb-4">Crée un nouveau dossier dans SocialAC.</p>
            
            <h4 className="font-semibold text-gray-900 mb-2">Exemple de requête</h4>
            <CodeBlock id="create" code={`curl -X POST "${baseUrl}/api/webhook/dossiers" \\
  -H "X-API-Key: sk_live_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "reference_externe": "CRM-12345",
    "nom": "DUPONT",
    "prenom": "Marie",
    "adresse": "12 rue des Lilas",
    "code_postal": "75001",
    "ville": "Paris",
    "telephone": "0612345678",
    "caisse": "AG2R La Mondiale",
    "motif": "Aide au logement"
  }'`} />

            <h4 className="font-semibold text-gray-900 mt-4 mb-2">Réponse</h4>
            <CodeBlock id="create-response" code={`{
  "success": true,
  "dossier_id": "uuid-du-dossier",
  "reference": "API-2026-0001",
  "message": "Dossier créé avec succès"
}`} />
          </Section>

          <Section id="status" title="GET /dossiers/{reference} - Récupérer le statut">
            <p className="text-gray-600 mb-4">Retourne le statut actuel d'un dossier.</p>
            
            <CodeBlock id="status" code={`curl -X GET "${baseUrl}/api/webhook/dossiers/API-2026-0001" \\
  -H "X-API-Key: sk_live_votre_cle"`} />

            <h4 className="font-semibold text-gray-900 mt-4 mb-2">Réponse</h4>
            <CodeBlock id="status-response" code={`{
  "reference": "API-2026-0001",
  "reference_externe": "CRM-12345",
  "statut": "pending_docs",
  "statut_label": "En attente de documents",
  "priorite": "med",
  "date_creation": "2026-03-10T12:00:00",
  "gestionnaire": "Sophie Martin"
}`} />
          </Section>

          <Section id="comments" title="GET /dossiers/{reference}/commentaires - Lister les commentaires">
            <p className="text-gray-600 mb-4">Retourne tous les commentaires associés au dossier.</p>
            
            <CodeBlock id="comments" code={`curl -X GET "${baseUrl}/api/webhook/dossiers/API-2026-0001/commentaires" \\
  -H "X-API-Key: sk_live_votre_cle"`} />
          </Section>

          <Section id="add-comment" title="POST /dossiers/{reference}/commentaires - Ajouter un commentaire">
            <p className="text-gray-600 mb-4">Ajoute un commentaire à un dossier depuis votre système.</p>
            
            <CodeBlock id="add-comment" code={`curl -X POST "${baseUrl}/api/webhook/dossiers/API-2026-0001/commentaires" \\
  -H "X-API-Key: sk_live_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Information du partenaire",
    "auteur": "CRM Partenaire"
  }'`} />
          </Section>

          <Section id="list" title="GET /dossiers - Lister les dossiers">
            <p className="text-gray-600 mb-4">Retourne la liste des dossiers avec pagination et filtrage.</p>
            
            <h4 className="font-semibold text-gray-900 mb-2">Paramètres</h4>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li><code>statut</code> - Filtrer par statut (new, inprog, pending_docs, etc.)</li>
              <li><code>limit</code> - Nombre de résultats (défaut: 50)</li>
              <li><code>offset</code> - Position de départ pour pagination</li>
            </ul>
            
            <CodeBlock id="list" code={`curl -X GET "${baseUrl}/api/webhook/dossiers?statut=pending_docs&limit=10" \\
  -H "X-API-Key: sk_live_votre_cle"`} />
          </Section>
        </div>

        {/* Codes d'erreur */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">⚠️ Codes d'erreur</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <span className="px-3 py-1 bg-green-500 text-white rounded font-mono text-sm">200</span>
              <span className="text-gray-700">Succès</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
              <span className="px-3 py-1 bg-red-500 text-white rounded font-mono text-sm">401</span>
              <span className="text-gray-700">Clé API invalide</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
              <span className="px-3 py-1 bg-orange-500 text-white rounded font-mono text-sm">403</span>
              <span className="text-gray-700">Permission insuffisante</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <span className="px-3 py-1 bg-yellow-500 text-white rounded font-mono text-sm">404</span>
              <span className="text-gray-700">Dossier non trouvé</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <span className="px-3 py-1 bg-purple-500 text-white rounded font-mono text-sm">422</span>
              <span className="text-gray-700">Données invalides</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
              <span className="px-3 py-1 bg-gray-500 text-white rounded font-mono text-sm">500</span>
              <span className="text-gray-700">Erreur serveur</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-8 text-gray-400">
          <p>Documentation API SocialAC v2.0 - Mars 2026</p>
        </div>
      </div>
    </div>
  );
};

export default APIDocumentation;
